// ** React Imports
import { useEffect, useMemo } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";

// ** Selector Components
import MenuItems from "./components/MenuItems";
import { PreferencesActivatorChildMulti } from "./components/PreferencesActivatorChildMulti";

// ** Custom Hooks
import useOpenClose from "hooks/useOpenClose";

// ** Utils & Helpers
import slug from "utils/slug";
import collatorSort from "utils/collatorSort";

// #####################################################
export const PreferencesSelectorChildMulti = ({
	label,
	rawItems,
	menuFontSize,
	withSearchInput,
	withAdditionalInfo,
	allwaysSelected,
	searchValue,
	handleSelectItem,
	checkedItemsArray,
}) => {
	const { t } = useTranslation(["filtersAndSelectors"]);

	const language = useSelector((state) => state.globalCommon.language);
	const { isOpen, close, open, toggle } = useOpenClose();

	const items = useMemo(() => {
		return rawItems.map((item) => ({
			key: item.key,
			code: item.code,
			content: t(`filtersAndSelectors:preferences.${item.key}`),
			text: t(`filtersAndSelectors:preferences.${item.key}`),
			meta: {
				url: String(item.code),
			},
			value: item.key,
			selected: !checkedItemsArray.includes(item.key),
		}));
	}, [rawItems, checkedItemsArray, language]);

	const sortedItems = useMemo(() => {
		return collatorSort(collatorSort(items, "content"), "selected");
	}, [items]);

	const searchSlug = useMemo(() => {
		return searchValue.length > 0
			? slug(searchValue.toLowerCase(), " ")
			: "";
	}, [searchValue]);

	const filteredItems = useMemo(() => {
		if (!searchSlug) return sortedItems;

		return sortedItems.filter((item) => {
			const itemSlug = slug(item.content, " ").toLowerCase();
			return itemSlug.includes(searchSlug);
		});
	}, [sortedItems, searchSlug]);

	const finalItems = useMemo(() => {
		const labelSlug = label
			? slug(label.props?.children?.[1] || String(label))
			: "";
		return filteredItems.map((item, index) => ({
			...item,
			key: labelSlug
				? `${labelSlug}-item-${item.key || index}`
				: `item-${item.key || index}`,
		}));
	}, [searchValue.length, isOpen, language]);

	const isSelected = useMemo(() => {
		return checkedItemsArray.some((checkedId) =>
			items.some(
				(item) => item.key === checkedId || item.code === checkedId
			)
		);
	}, [checkedItemsArray, items]);

	// #####################################################

	useEffect(() => {
		if (searchValue.length > 0 && finalItems.length > 0) {
			open();
		} else if (finalItems.length === 0 || searchValue.length === 0) {
			close();
		}
	}, [searchValue]);

	const selectedItems = useMemo(
		() =>
			sortedItems.filter((item) =>
				checkedItemsArray.includes(item.value)
			),
		[finalItems, checkedItemsArray, language]
	);

	// #####################################################
	if (filteredItems.length === 0) {
		return null;
	}
	return (
		<PreferencesActivatorChildMulti
			label={label}
			onClick={() => {
				toggle();
			}}
			isOpen={isOpen}
			isSelected={isSelected}
			selected={selectedItems}
		>
			{isOpen && (
				<MenuItems
					withSearchInput={withSearchInput}
					finalItems={finalItems}
					selected={checkedItemsArray}
					handleSelectItem={handleSelectItem}
					menuFontSize={menuFontSize}
					allwaysSelected={allwaysSelected}
					withAdditionalInfo={withAdditionalInfo}
					multiple
					$customPadding={"0.25rem 0 0.5rem 0"}
				/>
			)}
		</PreferencesActivatorChildMulti>
	);
};
